import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, testsAPI, domainsAPI } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import CurlImporter from './CurlImporter';
import PostmanImporter from './PostmanImporter';
import TestProgress from './TestProgress';
import TestResults from './TestResults';
import Loader from '../Common/Loader';
import ErrorMessage from '../Common/ErrorMessage';
import {
    trackNewTestPageView,
    trackCurlImport,
    trackPostmanImport,
    trackLoadProfileSelected,
    trackHttpMethodChanged,
    trackAuthTypeChanged,
    trackTestSubmitted,
    trackTestStartError,
    trackDomainVerificationShown,
    trackDomainVerificationAttempt,
    trackDomainVerified,
    trackTestProgressView,
} from '../../utils/analytics';

const LOAD_PROFILES = [
    {
        id: 'fixed',
        name: 'Fixed',
        description: 'Maximum VUs used throughout the test',
        icon: '▬',
    },
    {
        id: 'rampup',
        name: 'Ramp Up',
        description: 'VUs increase from initial to maximum during ramp up period',
        icon: '📈',
    },
    {
        id: 'spike',
        name: 'Spike',
        description: 'VUs spike from base load to maximum then return to base',
        icon: '⚡',
    },
    {
        id: 'peak',
        name: 'Peak',
        description: 'VUs increase to maximum, hold steady, then decrease to base',
        icon: '🏔️',
    },
];

const NewTest = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { user, checkAuth } = useAuth();

    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Test configuration  
    const [url, setUrl] = useState('');
    const [method, setMethod] = useState('GET');
    const [headers, setHeaders] = useState('');
    const [body, setBody] = useState('');
    const [authType, setAuthType] = useState('none');
    const [authToken, setAuthToken] = useState('');
    const [authKey, setAuthKey] = useState('');
    const [authValue, setAuthValue] = useState('');
    const [connections, setConnections] = useState(10);
    const [duration, setDuration] = useState(10);

    // Load profile configuration
    const [loadProfile, setLoadProfile] = useState('fixed');
    const [initialLoad, setInitialLoad] = useState(1);
    const [baseLoad, setBaseLoad] = useState(2);
    const [rampUpTime, setRampUpTime] = useState(5);
    const [spikeTime, setSpikeTime] = useState(3);
    const [peakHoldTime, setPeakHoldTime] = useState(5);

    // Test execution  
    const [testId, setTestId] = useState(null);
    const [testStatus, setTestStatus] = useState(null);
    const [testResults, setTestResults] = useState(null);

    // Domain verification popup state
    const [showVerifyPopup, setShowVerifyPopup] = useState(false);
    const [unverifiedDomain, setUnverifiedDomain] = useState('');
    const [domainData, setDomainData] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null);
    const [addingDomain, setAddingDomain] = useState(false);
    const [polling, setPolling] = useState(false);

    const fetchProject = useCallback(async () => {
        try {
            setLoading(true);
            const response = await projectsAPI.getById(projectId);
            setProject(response.data.project);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load project');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchProject();
    }, [fetchProject]);

    useEffect(() => {
        trackNewTestPageView(projectId);
    }, [projectId]);

    const checkTestStatus = useCallback(async () => {
        if (!testId) return;

        try {
            const response = await testsAPI.getById(testId);
            const test = response.data.test;
            setTestStatus(test.status);

            if (test.status === 'completed') {
                setTestResults(test.results);
                setPolling(false);
                await checkAuth(); // Refresh user credits  
            } else if (test.status === 'failed') {
                setError(test.error || 'Test failed');
                setPolling(false);
            }
        } catch (err) {
            console.error('Failed to check test status:', err);
        }
    }, [testId, checkAuth]);

    useEffect(() => {
        if (testId && polling) {
            const interval = setInterval(() => {
                checkTestStatus();
            }, 2000);

            return () => clearInterval(interval);
        }
    }, [testId, polling, checkTestStatus]);

    const handleImportCurl = (curlData) => {
        setUrl(curlData.url);
        setMethod(curlData.method);
        if (curlData.headers) {
            setHeaders(JSON.stringify(curlData.headers, null, 2));
        }
        if (curlData.body) {
            setBody(curlData.body);
        }
        trackCurlImport();
    };

    const handleImportPostman = (postmanData) => {
        setUrl(postmanData.url);
        setMethod(postmanData.method);
        if (postmanData.headers) {
            setHeaders(JSON.stringify(postmanData.headers, null, 2));
        }
        if (postmanData.body) {
            setBody(postmanData.body);
        }
        // Handle authentication
        if (postmanData.authType) {
            setAuthType(postmanData.authType);
            if (postmanData.authType === 'bearer' && postmanData.authToken) {
                setAuthToken(postmanData.authToken);
            } else if (postmanData.authType === 'header') {
                if (postmanData.authKey) setAuthKey(postmanData.authKey);
                if (postmanData.authValue) setAuthValue(postmanData.authValue);
            }
        }
        trackPostmanImport();
    };

    const handleRunTest = async (e) => {
        e.preventDefault();
        setError('');

        try {
            // Parse headers  
            let parsedHeaders = {};
            if (headers.trim()) {
                try {
                    parsedHeaders = JSON.parse(headers);
                } catch {
                    setError('Invalid JSON in headers');
                    return;
                }
            }

            // Prepare auth config  
            let auth = null;
            if (authType === 'bearer') {
                auth = { type: 'bearer', token: authToken };
            } else if (authType === 'header') {
                auth = { type: 'header', key: authKey, value: authValue };
            }

            // Prepare test config  
            const config = {
                url,
                method,
                headers: parsedHeaders,
                body: body || undefined,
                auth,
                connections: parseInt(connections),
                duration: parseInt(duration),
                loadProfile,
                initialLoad: parseInt(initialLoad),
                baseLoad: parseInt(baseLoad),
                rampUpTime: parseInt(rampUpTime),
                spikeTime: parseInt(spikeTime),
                peakHoldTime: parseInt(peakHoldTime),
            };

            // Create test
            const response = await testsAPI.create({
                projectId,
                config,
            });

            setTestId(response.data.test.id);
            setTestStatus('running');
            setPolling(true);
            trackTestSubmitted({ method, loadProfile, connections, duration });
            trackTestProgressView();
        } catch (err) {
            const errorData = err.response?.data;
            // Check for domain verification error - show popup instead of banner
            if (errorData?.error === 'Domain not verified') {
                setUnverifiedDomain(errorData.domain);
                setShowVerifyPopup(true);
                setVerifyResult(null);
                setDomainData(null);
                trackDomainVerificationShown(errorData.domain);
                // Try to fetch existing domain data
                fetchDomainData(errorData.domain);
            } else {
                trackTestStartError(errorData?.error || 'Failed to start test');
                setError(errorData?.error || 'Failed to start test');
            }
        }
    };

    // Fetch existing domain data if it exists
    const fetchDomainData = async (domainName) => {
        try {
            const response = await domainsAPI.getAll();
            const existingDomain = response.data.domains?.find(
                d => d.domain === domainName || domainName.endsWith(d.domain)
            );
            if (existingDomain) {
                setDomainData(existingDomain);
            }
        } catch (err) {
            console.error('Failed to fetch domains:', err);
        }
    };

    // Add domain for verification
    const handleAddDomain = async () => {
        try {
            setAddingDomain(true);
            const response = await domainsAPI.create(unverifiedDomain);
            setDomainData(response.data.domain);
            setVerifyResult(null);
        } catch (err) {
            setVerifyResult({
                success: false,
                message: err.response?.data?.error || 'Failed to add domain'
            });
        } finally {
            setAddingDomain(false);
        }
    };

    // Verify domain ownership
    const handleVerifyDomain = async () => {
        if (!domainData?._id) return;

        try {
            setVerifying(true);
            setVerifyResult(null);
            const response = await domainsAPI.verify(domainData._id);
            setVerifyResult(response.data);
            trackDomainVerificationAttempt(unverifiedDomain);
            if (response.data.success) {
                trackDomainVerified(unverifiedDomain);
                // Domain verified! Close popup and retry test
                setTimeout(() => {
                    setShowVerifyPopup(false);
                    setUnverifiedDomain('');
                    setDomainData(null);
                    setVerifyResult(null);
                }, 1500);
            }
        } catch (err) {
            setVerifyResult({
                success: false,
                message: err.response?.data?.message || 'Verification failed. Please check your DNS or file setup.'
            });
        } finally {
            setVerifying(false);
        }
    };

    // Close verification popup
    const closeVerifyPopup = () => {
        setShowVerifyPopup(false);
        setUnverifiedDomain('');
        setDomainData(null);
        setVerifyResult(null);
    };

    if (loading) {
        return <Loader message="Loading project..." />;
    }

    if (!project) {
        return <ErrorMessage message="Project not found" />;
    }

    if (testStatus === 'running') {
        return <TestProgress />;
    }

    if (testStatus === 'completed' && testResults) {
        return (
            <TestResults
                results={testResults}
                testId={testId}
                onRunAnother={() => {
                    setTestId(null);
                    setTestStatus(null);
                    setTestResults(null);
                    setPolling(false);
                }}
                onBackToProject={() => navigate(`/project/${projectId}`)}
            />
        );
    }

    return (
        <div className="fade-in">
            <style>{`
        .new-test-header {
          margin-bottom: 40px;
        }
        
        .new-test-title {
          font-family: "Agrandir Narrow", "Agrandir", Arial, sans-serif;
          font-size: 36px;
          font-weight: 700;
          font-style: normal;
          margin-bottom: 8px;
          color: var(--text-primary);
        }
        
        .new-test-subtitle {
          color: var(--text-secondary);
          font-size: 15px;
        }
        
        .section-title {
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 20px;
          color: var(--text-primary);
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        .params-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        
        .credit-info {
          display: flex;
          justify-content: space-between;
          padding: 16px;
          background: var(--light-gray);
          border-radius: 4px;
          margin-top: 20px;
          border: 1px solid var(--border-color);
        }
        
        .warning-banner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          background: rgba(239, 68, 68, 0.1);
          border-radius: 8px;
          margin-top: 16px;
          border: 1px solid rgba(239, 68, 68, 0.3);
          gap: 16px;
        }
        
        .warning-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .form-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }
        
        @media (max-width: 768px) {
          .new-test-title {
            font-size: 28px;
          }
          
          .params-grid {
            grid-template-columns: 1fr;
            gap: 0;
          }
          
          .credit-info {
            flex-direction: column;
            gap: 8px;
            text-align: center;
          }
          
          .warning-banner {
            flex-direction: column;
            text-align: center;
            gap: 12px;
          }

          .warning-content {
            flex-direction: column;
            text-align: center;
            gap: 8px;
          }

          .warning-banner button {
            width: 100%;
          }
          
          .form-actions {
            flex-direction: column-reverse;
          }
          
          .form-actions button {
            width: 100%;
          }
        }
        
        @media (max-width: 480px) {
          .new-test-title {
            font-size: 24px;
          }
        }
      `}</style>

            <div className="new-test-header">
                <div>
                    <button
                        className="btn btn-secondary btn-small"
                        onClick={() => navigate(`/project/${projectId}`)}
                        style={{ marginBottom: '10px' }}
                    >
                        ← Back to Project
                    </button>
                    <h1 className="new-test-title">New Load Test</h1>
                    <p className="new-test-subtitle">{project.name}</p>
                </div>
            </div>

            {error && <ErrorMessage message={error} />}

            <div className="card">
                <CurlImporter onImport={handleImportCurl} />

                <div style={{ margin: '24px 0', borderTop: '1px solid var(--border-color)', paddingTop: '24px' }}>
                    <PostmanImporter onImport={handleImportPostman} />
                </div>
            </div>

            <form onSubmit={handleRunTest}>
                <div className="card">
                    <h3 className="section-title">Request Configuration</h3>

                    <div className="input-group">
                        <label htmlFor="method">HTTP Method</label>
                        <select
                            id="method"
                            value={method}
                            onChange={(e) => { setMethod(e.target.value); trackHttpMethodChanged(e.target.value); }}
                            required
                        >
                            <option value="GET">GET</option>
                            <option value="POST">POST</option>
                            <option value="PUT">PUT</option>
                            <option value="PATCH">PATCH</option>
                            <option value="DELETE">DELETE</option>
                        </select>
                    </div>

                    <div className="input-group">
                        <label htmlFor="url">URL</label>
                        <input
                            id="url"
                            type="url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://api.example.com/endpoint"
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label htmlFor="headers">Headers (JSON)</label>
                        <textarea
                            id="headers"
                            value={headers}
                            onChange={(e) => setHeaders(e.target.value)}
                            placeholder={'{\n  "Content-Type": "application/json"\n}'}
                            style={{ fontFamily: 'monospace' }}
                        />
                    </div>

                    {['POST', 'PUT', 'PATCH'].includes(method) && (
                        <div className="input-group">
                            <label htmlFor="body">Request Body</label>
                            <textarea
                                id="body"
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                placeholder={'{\n  "key": "value"\n}'}
                                style={{ fontFamily: 'monospace' }}
                            />
                        </div>
                    )}
                </div>

                <div className="card">
                    <h3 className="section-title">Authentication</h3>

                    <div className="input-group">
                        <label htmlFor="authType">Authentication Type</label>
                        <select
                            id="authType"
                            value={authType}
                            onChange={(e) => { setAuthType(e.target.value); trackAuthTypeChanged(e.target.value); }}
                        >
                            <option value="none">None</option>
                            <option value="bearer">Bearer Token</option>
                            <option value="header">Custom Header</option>
                        </select>
                    </div>

                    {authType === 'bearer' && (
                        <div className="input-group">
                            <label htmlFor="authToken">Bearer Token</label>
                            <input
                                id="authToken"
                                type="text"
                                value={authToken}
                                onChange={(e) => setAuthToken(e.target.value)}
                                placeholder="Enter your bearer token"
                            />
                        </div>
                    )}

                    {authType === 'header' && (
                        <>
                            <div className="input-group">
                                <label htmlFor="authKey">Header Key</label>
                                <input
                                    id="authKey"
                                    type="text"
                                    value={authKey}
                                    onChange={(e) => setAuthKey(e.target.value)}
                                    placeholder="e.g., X-API-Key"
                                />
                            </div>
                            <div className="input-group">
                                <label htmlFor="authValue">Header Value</label>
                                <input
                                    id="authValue"
                                    type="text"
                                    value={authValue}
                                    onChange={(e) => setAuthValue(e.target.value)}
                                    placeholder="Enter header value"
                                />
                            </div>
                        </>
                    )}
                </div>

                <div className="card">
                    <h3 className="section-title">Test Parameters</h3>

                    <div className="params-grid">
                        <div className="input-group">
                            <label htmlFor="connections">Concurrent VUs (Virtual Users)</label>
                            <input
                                id="connections"
                                type="number"
                                value={connections}
                                onChange={(e) => setConnections(e.target.value)}
                                min="1"
                                max={999}
                                required
                            />
                            <small style={{ display: 'block', marginTop: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                Max: 999 VUs
                            </small>
                        </div>

                        <div className="input-group">
                            <label htmlFor="duration">Duration (seconds)</label>
                            <input
                                id="duration"
                                type="number"
                                value={duration}
                                onChange={(e) => setDuration(e.target.value)}
                                min="1"
                                max={300}
                                required
                            />
                            <small style={{ display: 'block', marginTop: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                Max: 5 minutes (300 seconds)
                            </small>
                        </div>
                    </div>

                    <div className="credit-info">
                        <span>Credits per Test: <strong style={{ color: 'var(--text-primary)' }}>1</strong></span>
                        <span>Your Balance: <strong style={{ color: (user?.credits || 0) === 0 ? 'var(--error-color)' : 'var(--text-primary)' }}>{user?.credits || 0}</strong></span>
                    </div>

                    {(user?.credits || 0) === 0 && (
                        <div className="warning-banner">
                            <div className="warning-content">
                                <span style={{ fontSize: '24px' }}>⚠️</span>
                                <div>
                                    <strong>You have no credits remaining!</strong>
                                    <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'var(--text-secondary)' }}>Purchase more credits to continue running load tests.</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-gold"
                                onClick={() => window.open('http://subscribe.apiloadtest.com', '_blank')}
                            >
                                Buy Credits
                            </button>
                        </div>
                    )}
                </div>

                {/* Load Profile Section */}
                <div className="card">
                    <style>{`
            .load-profile-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 16px;
              margin-bottom: 24px;
            }
            
            .load-profile-card {
              border: 2px solid var(--border-color);
              border-radius: 12px;
              padding: 16px;
              cursor: pointer;
              transition: all 0.2s ease;
              background: var(--card-bg);
            }
            
            .load-profile-card:hover {
              border-color: var(--text-secondary);
              transform: translateY(-2px);
            }
            
            .load-profile-card.selected {
              border-color: var(--primary);
              background: linear-gradient(135deg, rgba(90, 169, 230, 0.1) 0%, rgba(127, 200, 248, 0.1) 100%);
            }
            
            .load-profile-header {
              display: flex;
              align-items: center;
              gap: 10px;
              margin-bottom: 8px;
            }
            
            .load-profile-icon {
              font-size: 24px;
            }
            
            .load-profile-name {
              font-weight: 600;
              font-size: 16px;
              color: var(--text-primary);
            }
            
            .load-profile-desc {
              font-size: 12px;
              color: var(--text-secondary);
              line-height: 1.4;
            }
            
            .load-profile-preview {
              margin-top: 12px;
              height: 50px;
              background: var(--light-gray);
              border-radius: 6px;
              overflow: hidden;
            }
            
            .load-profile-preview svg {
              width: 100%;
              height: 100%;
            }
            
            .profile-config {
              margin-top: 16px;
              padding-top: 16px;
              border-top: 1px solid var(--border-color);
            }
            
            .profile-config-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
              gap: 16px;
            }
            
            .range-input-group {
              display: flex;
              flex-direction: column;
              gap: 8px;
            }
            
            .range-input-group label {
              font-size: 13px;
              color: var(--text-secondary);
            }
            
            .range-input-group input[type="range"] {
              width: 100%;
              height: 6px;
              -webkit-appearance: none;
              background: var(--border-color);
              border-radius: 3px;
              outline: none;
            }
            
            .range-input-group input[type="range"]::-webkit-slider-thumb {
              -webkit-appearance: none;
              width: 18px;
              height: 18px;
              background: var(--primary);
              border-radius: 50%;
              cursor: pointer;
              transition: background 0.2s;
            }
            
            .range-input-group input[type="range"]::-webkit-slider-thumb:hover {
              background: var(--primary-dark, #4a99d6);
            }
            
            .range-value {
              font-size: 14px;
              font-weight: 600;
              color: var(--text-primary);
            }
            
            @media (max-width: 768px) {
              .load-profile-grid {
                grid-template-columns: 1fr 1fr;
              }
              
              .profile-config-grid {
                grid-template-columns: 1fr;
              }
            }
            
            @media (max-width: 480px) {
              .load-profile-grid {
                grid-template-columns: 1fr;
              }
            }
          `}</style>

                    <h3 className="section-title">Load Profile</h3>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '14px' }}>
                        Choose how the virtual users should be distributed during the test.
                    </p>

                    <div className="load-profile-grid">
                        {LOAD_PROFILES.map((profile) => (
                            <div
                                key={profile.id}
                                className={`load-profile-card ${loadProfile === profile.id ? 'selected' : ''}`}
                                onClick={() => { setLoadProfile(profile.id); trackLoadProfileSelected(profile.id); }}
                            >
                                <div className="load-profile-header">
                                    <span className="load-profile-icon">{profile.icon}</span>
                                    <span className="load-profile-name">{profile.name}</span>
                                </div>
                                <p className="load-profile-desc">{profile.description}</p>
                                <div className="load-profile-preview">
                                    {profile.id === 'fixed' && (
                                        <svg viewBox="0 0 100 50" preserveAspectRatio="none">
                                            <path
                                                d="M0 15 L100 15"
                                                stroke="#5aa9e6"
                                                strokeWidth="3"
                                                fill="none"
                                            />
                                            <path
                                                d="M0 15 L100 15 L100 50 L0 50 Z"
                                                fill="rgba(90, 169, 230, 0.2)"
                                            />
                                        </svg>
                                    )}
                                    {profile.id === 'rampup' && (
                                        <svg viewBox="0 0 100 50" preserveAspectRatio="none">
                                            <path
                                                d="M0 45 L50 15 L100 15"
                                                stroke="#5aa9e6"
                                                strokeWidth="3"
                                                fill="none"
                                            />
                                            <path
                                                d="M0 45 L50 15 L100 15 L100 50 L0 50 Z"
                                                fill="rgba(90, 169, 230, 0.2)"
                                            />
                                        </svg>
                                    )}
                                    {profile.id === 'spike' && (
                                        <svg viewBox="0 0 100 50" preserveAspectRatio="none">
                                            <path
                                                d="M0 35 L35 35 L50 10 L65 35 L100 35"
                                                stroke="#5aa9e6"
                                                strokeWidth="3"
                                                fill="none"
                                            />
                                            <path
                                                d="M0 35 L35 35 L50 10 L65 35 L100 35 L100 50 L0 50 Z"
                                                fill="rgba(90, 169, 230, 0.2)"
                                            />
                                        </svg>
                                    )}
                                    {profile.id === 'peak' && (
                                        <svg viewBox="0 0 100 50" preserveAspectRatio="none">
                                            <path
                                                d="M0 40 L20 40 L35 10 L65 10 L80 40 L100 40"
                                                stroke="#5aa9e6"
                                                strokeWidth="3"
                                                fill="none"
                                            />
                                            <path
                                                d="M0 40 L20 40 L35 10 L65 10 L80 40 L100 40 L100 50 L0 50 Z"
                                                fill="rgba(90, 169, 230, 0.2)"
                                            />
                                        </svg>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Profile-specific configuration */}
                    {loadProfile === 'rampup' && (
                        <div className="profile-config">
                            <h4 style={{ fontSize: '14px', marginBottom: '16px', color: 'var(--text-primary)' }}>Ramp Up Configuration</h4>
                            <div className="profile-config-grid">
                                <div className="range-input-group">
                                    <label>Initial Load (VUs)</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max={Math.max(1, parseInt(connections) - 1)}
                                        value={initialLoad}
                                        onChange={(e) => setInitialLoad(e.target.value)}
                                    />
                                    <span className="range-value">{initialLoad} VUs</span>
                                </div>
                                <div className="range-input-group">
                                    <label>Ramp Up Duration (seconds)</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max={Math.max(1, parseInt(duration) - 1)}
                                        value={rampUpTime}
                                        onChange={(e) => setRampUpTime(e.target.value)}
                                    />
                                    <span className="range-value">{rampUpTime}s</span>
                                </div>
                            </div>
                            <p style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                VUs will increase from {initialLoad} to {connections} over {rampUpTime} seconds, then hold at {connections} VUs for the remaining {Math.max(0, parseInt(duration) - parseInt(rampUpTime))} seconds.
                            </p>
                        </div>
                    )}

                    {loadProfile === 'spike' && (
                        <div className="profile-config">
                            <h4 style={{ fontSize: '14px', marginBottom: '16px', color: 'var(--text-primary)' }}>Spike Configuration</h4>
                            <div className="profile-config-grid">
                                <div className="range-input-group">
                                    <label>Base Load (VUs)</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max={Math.max(1, parseInt(connections) - 1)}
                                        value={baseLoad}
                                        onChange={(e) => setBaseLoad(e.target.value)}
                                    />
                                    <span className="range-value">{baseLoad} VUs</span>
                                </div>
                                <div className="range-input-group">
                                    <label>Spike Duration (seconds)</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max={Math.max(1, Math.floor(parseInt(duration) / 2))}
                                        value={spikeTime}
                                        onChange={(e) => setSpikeTime(e.target.value)}
                                    />
                                    <span className="range-value">{spikeTime}s</span>
                                </div>
                            </div>
                            <p style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                VUs will start at {baseLoad}, spike to {connections} for {spikeTime} seconds, then return to {baseLoad}.
                            </p>
                        </div>
                    )}

                    {loadProfile === 'peak' && (
                        <div className="profile-config">
                            <h4 style={{ fontSize: '14px', marginBottom: '16px', color: 'var(--text-primary)' }}>Peak Configuration</h4>
                            <div className="profile-config-grid">
                                <div className="range-input-group">
                                    <label>Base Load (VUs)</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max={Math.max(1, parseInt(connections) - 1)}
                                        value={baseLoad}
                                        onChange={(e) => setBaseLoad(e.target.value)}
                                    />
                                    <span className="range-value">{baseLoad} VUs</span>
                                </div>
                                <div className="range-input-group">
                                    <label>Peak Hold Duration (seconds)</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max={Math.max(1, Math.floor(parseInt(duration) / 2))}
                                        value={peakHoldTime}
                                        onChange={(e) => setPeakHoldTime(e.target.value)}
                                    />
                                    <span className="range-value">{peakHoldTime}s</span>
                                </div>
                            </div>
                            <p style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                VUs will increase from {baseLoad} to {connections}, hold at peak for {peakHoldTime} seconds, then decrease back to {baseLoad}.
                            </p>
                        </div>
                    )}

                    {loadProfile === 'fixed' && (
                        <div className="profile-config">
                            <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                All {connections} virtual users will be active throughout the entire {duration} second test duration.
                            </p>
                        </div>
                    )}
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate(`/project/${projectId}`)}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!url || (user?.credits || 0) === 0}
                    >
                        🚀 Run Load Test
                    </button>
                </div>
            </form>

            {/* Domain Verification Popup */}
            {showVerifyPopup && (
                <>
                    <style>{`
                        .verify-popup-overlay {
                            position: fixed;
                            top: 0;
                            left: 0;
                            right: 0;
                            bottom: 0;
                            background: rgba(0, 0, 0, 0.6);
                            backdrop-filter: blur(4px);
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            z-index: 1000;
                            padding: 20px;
                        }

                        .verify-popup {
                            background: var(--card-bg);
                            border: 1px solid var(--border-color);
                            border-radius: 16px;
                            max-width: 560px;
                            width: 100%;
                            max-height: 90vh;
                            overflow-y: auto;
                            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                            animation: popupSlideIn 0.3s ease-out;
                        }

                        @keyframes popupSlideIn {
                            from {
                                opacity: 0;
                                transform: scale(0.95) translateY(-10px);
                            }
                            to {
                                opacity: 1;
                                transform: scale(1) translateY(0);
                            }
                        }

                        .verify-popup-header {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 20px 24px;
                            border-bottom: 1px solid var(--border-color);
                        }

                        .verify-popup-title {
                            font-size: 20px;
                            font-weight: 700;
                            color: var(--text-primary);
                            display: flex;
                            align-items: center;
                            gap: 10px;
                        }

                        .verify-popup-close {
                            background: none;
                            border: none;
                            font-size: 24px;
                            cursor: pointer;
                            color: var(--text-secondary);
                            padding: 4px;
                            line-height: 1;
                            transition: color 0.2s;
                        }

                        .verify-popup-close:hover {
                            color: var(--text-primary);
                        }

                        .verify-popup-body {
                            padding: 24px;
                        }

                        .verify-domain-name {
                            background: rgba(239, 68, 68, 0.1);
                            border: 1px solid rgba(239, 68, 68, 0.3);
                            border-radius: 8px;
                            padding: 12px 16px;
                            margin-bottom: 20px;
                            display: flex;
                            align-items: center;
                            gap: 10px;
                        }

                        .verify-domain-name span {
                            font-weight: 600;
                            color: var(--text-primary);
                            font-family: monospace;
                        }

                        .verify-section {
                            background: var(--bg-secondary);
                            border: 1px solid var(--border-color);
                            border-radius: 10px;
                            padding: 16px;
                            margin-bottom: 16px;
                        }

                        .verify-method {
                            margin-bottom: 16px;
                        }

                        .verify-method:last-child {
                            margin-bottom: 0;
                        }

                        .verify-method h4 {
                            font-size: 14px;
                            font-weight: 600;
                            color: var(--text-primary);
                            margin-bottom: 8px;
                            display: flex;
                            align-items: center;
                            gap: 8px;
                        }

                        .verify-method p {
                            font-size: 13px;
                            color: var(--text-secondary);
                            margin-bottom: 8px;
                            line-height: 1.5;
                        }

                        .verify-code-block {
                            background: var(--bg-tertiary);
                            border: 1px solid var(--border-color);
                            border-radius: 6px;
                            padding: 10px 14px;
                            font-family: 'Monaco', 'Menlo', monospace;
                            font-size: 12px;
                            color: var(--text-primary);
                            word-break: break-all;
                            line-height: 1.5;
                        }

                        .verify-result-box {
                            margin-top: 16px;
                            padding: 14px 16px;
                            border-radius: 8px;
                            font-size: 14px;
                            display: flex;
                            align-items: center;
                            gap: 10px;
                        }

                        .verify-result-box.success {
                            background: rgba(16, 185, 129, 0.1);
                            border: 1px solid #10b981;
                            color: #10b981;
                        }

                        .verify-result-box.error {
                            background: rgba(239, 68, 68, 0.1);
                            border: 1px solid #ef4444;
                            color: #ef4444;
                        }

                        .verify-popup-footer {
                            display: flex;
                            gap: 12px;
                            justify-content: flex-end;
                            padding: 20px 24px;
                            border-top: 1px solid var(--border-color);
                        }

                        .add-domain-prompt {
                            text-align: center;
                            padding: 24px;
                        }

                        .add-domain-prompt p {
                            color: var(--text-secondary);
                            margin-bottom: 16px;
                            font-size: 14px;
                        }

                        @media (max-width: 600px) {
                            .verify-popup {
                                max-height: 85vh;
                            }

                            .verify-popup-header {
                                padding: 16px;
                            }

                            .verify-popup-body {
                                padding: 16px;
                            }

                            .verify-popup-footer {
                                padding: 16px;
                                flex-direction: column-reverse;
                            }

                            .verify-popup-footer button {
                                width: 100%;
                            }
                        }
                    `}</style>
                    <div className="verify-popup-overlay" onClick={closeVerifyPopup}>
                        <div className="verify-popup" onClick={(e) => e.stopPropagation()}>
                            <div className="verify-popup-header">
                                <h2 className="verify-popup-title">
                                    🔐 Domain Verification Required
                                </h2>
                                <button className="verify-popup-close" onClick={closeVerifyPopup}>
                                    ×
                                </button>
                            </div>
                            <div className="verify-popup-body">
                                <div className="verify-domain-name">
                                    <span>⚠️</span>
                                    <span>{unverifiedDomain}</span>
                                </div>

                                {!domainData ? (
                                    <div className="add-domain-prompt">
                                        <p>
                                            This domain needs to be verified before you can run load tests.
                                            Click below to start the verification process.
                                        </p>
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleAddDomain}
                                            disabled={addingDomain}
                                        >
                                            {addingDomain ? 'Adding...' : 'Start Verification'}
                                        </button>
                                        {verifyResult && !verifyResult.success && (
                                            <div className="verify-result-box error">
                                                <span>✗</span>
                                                <span>{verifyResult.message}</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
                                            Verify ownership using one of the following methods:
                                        </p>

                                        <div className="verify-section">
                                            <div className="verify-method">
                                                <h4>📝 Option 1: DNS TXT Record</h4>
                                                <p>Add this TXT record to your domain's DNS settings:</p>
                                                <div className="verify-code-block">
                                                    alt-loadtest-verify={domainData.verificationToken}
                                                </div>
                                            </div>

                                            <div className="verify-method" style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                                                <h4>📁 Option 2: File Upload</h4>
                                                <p>Create a file at this path on your server:</p>
                                                <div className="verify-code-block" style={{ marginBottom: '8px' }}>
                                                    /.well-known/alt-loadtest.txt
                                                </div>
                                                <p>With this content:</p>
                                                <div className="verify-code-block">
                                                    {domainData.verificationToken}
                                                </div>
                                            </div>
                                        </div>

                                        {verifyResult && (
                                            <div className={`verify-result-box ${verifyResult.success ? 'success' : 'error'}`}>
                                                <span>{verifyResult.success ? '✓' : '✗'}</span>
                                                <span>{verifyResult.message}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="verify-popup-footer">
                                <button className="btn btn-secondary" onClick={closeVerifyPopup}>
                                    Cancel
                                </button>
                                {domainData && (
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleVerifyDomain}
                                        disabled={verifying || verifyResult?.success}
                                    >
                                        {verifying ? 'Checking...' : verifyResult?.success ? '✓ Verified!' : 'Check Verification'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default NewTest;
